import type { GetServerSideProps } from 'next'
import { useTranslations } from 'next-intl'
import type {
  StaticPageEntry,
  ContentfulStaticPageComponents,
  ContentfulStaticPageContent,
} from '@reward-platform/contentful'

import { Box } from '@alto-avios/alto-ui'
import { FLIGHT_FEATURE_FLAGS } from '@reward-platform/featureFlags'
import { type ProductSlug, ProductSlugSchema } from '~/schemas/browserUrl'
import { type FeatureFlagMap } from '~/schemas/featureFlags'
import { withPageAuthRequired } from '~/context/auth'
import Layout from '~/components/shared/Layout'
import {
  ContentfulHeroImage,
  normaliseHeroProps,
} from '~/components/contentful/ContentfulHeroImage'
import ContentfulPageSection from '~/components/contentful/ContentfulPageSection'
import ContentfulPageBanner from '~/components/contentful/ContentfulPageBanner'
import ContentfulAccordionSection from '~/components/contentful/ContentfulAccordionSection'
import ContentfulEducationalComponent from '~/components/contentful/ContentfulEducationalComponent'
import {
  ENABLED_FEATURE_PRODUCTS,
  getProductLegacyPath,
  ProductPartnershipLabel,
  SearchPageFormWrapper,
  SearchPageNavigation,
} from '~/components/search'
import { PromotionalBanners } from '~/components/shared/PromotionalBanners'
import usePromotionBanners from '~/hooks/usePromotionBanners/usePromotionBanners'
import { MediaQueryProvider } from '~/context/mediaQuery'

// Server-side imports
import { getPlatformBasePathFromRequest } from '@reward-platform/platform-config/server'
import { GenericException } from '@reward-platform/shared-api/exceptions'
import { fetchStaticPageContent } from '@reward-platform/contentful'
import { getAllFeatureVariablesForUser } from '~/server/featureFlags'
import { getOpCoIataFromHeaders } from '~/server/utils'
import { logger } from '~/server/logger'
import { isUserAgentMobileOrTablet } from '~/utils/windowNavigator'
import { isClient } from '~/utils/envChecks'
import styles from '~/components/search/SearchPage.module.scss'

interface ProductPageProps {
  activeSlug: ProductSlug
  content?: ContentfulStaticPageContent
  enabledSlugs: Array<ProductSlug>
}

const getEnabledProducts = (featureFlags: FeatureFlagMap): Record<ProductSlug, boolean> => ({
  flights: Boolean(featureFlags[FLIGHT_FEATURE_FLAGS.flights]?.value),
  hotels: true,
  cars: Boolean(featureFlags['car-hire']?.value),
  experiences: Boolean(featureFlags.experiences?.value),
})

const shouldRedirectToLegacySearch = (newHomePageEnabled: boolean, product?: ProductSlug) =>
  !newHomePageEnabled || (product !== undefined && !ENABLED_FEATURE_PRODUCTS.has(product))

const renderSectionComponent = (component: ContentfulStaticPageComponents) => {
  switch (component.contentType) {
    case 'component-banner':
      return <ContentfulPageBanner {...component} />
    case 'componentPageSection':
      return <ContentfulPageSection {...component} />
    case 'componentEducational':
      return <ContentfulEducationalComponent {...component} />
    case 'componentAccordion':
      return <ContentfulAccordionSection {...component} />
    default:
      return null
  }
}

// 100vw causes a horizontal scroll when small screen web view, so only add for mobile/tablet
const getLayoutWidth = () => {
  const userAgent = isClient() ? globalThis.navigator.userAgent : 'SSR'
  const isMobileOrTablet = isUserAgentMobileOrTablet(userAgent)
  return isMobileOrTablet ? '100vw' : '100%'
}

const ProductPage = ({ activeSlug: productType, content, enabledSlugs }: ProductPageProps) => {
  const banners = usePromotionBanners(productType, 'home')

  return (
    <MediaQueryProvider>
      <Box flexDirection="column" width={getLayoutWidth()} overflowY="hidden">
        {/* Defensive: Only render hero if present in Contentful content */}
        {content?.hero ? (
          <ContentfulHeroImage
            {...normaliseHeroProps(content.hero)}
            overlayContent={
              banners.length > 0 ? (
                <div className={banners.length === 1 ? styles.bannerWrapper : undefined}>
                  <PromotionalBanners banners={banners} isSingleBannerFullWidth />
                </div>
              ) : undefined
            }
          >
            <ProductPartnershipLabel productSlug={productType} />
            <SearchPageNavigation activeSlug={productType} enabledSlugs={enabledSlugs} />
            <SearchPageFormWrapper productSlug={productType} />
          </ContentfulHeroImage>
        ) : (
          <>
            <SearchPageNavigation activeSlug={productType} enabledSlugs={enabledSlugs} />
            <SearchPageFormWrapper productSlug={productType} />
          </>
        )}

        {content?.staticComponents?.map((component) => renderSectionComponent(component))}
      </Box>
    </MediaQueryProvider>
  )
}

export const getServerSideProps: GetServerSideProps<ProductPageProps> = async (context) => {
  try {
    const { product } = context.query
    const parseResult = ProductSlugSchema.safeParse(product)

    const opCo = getOpCoIataFromHeaders(context.req)
    const basePath = getPlatformBasePathFromRequest(context.req) ?? ''
    const featureFlagVariableMap = await getAllFeatureVariablesForUser(context.req, context.res)
    const newHomePageEnabled = Boolean(featureFlagVariableMap['homepage-contentful-rebuild']?.value)
    const enabledProducts = getEnabledProducts(featureFlagVariableMap)
    const enabledSlugs = Object.keys(enabledProducts).filter(
      (key) => enabledProducts[key as ProductSlug]
    ) as ProductSlug[]

    if (
      shouldRedirectToLegacySearch(
        newHomePageEnabled,
        parseResult.success ? parseResult.data : undefined
      )
    ) {
      const productSlug = parseResult.success ? parseResult.data : 'hotels'

      return {
        redirect: {
          destination: basePath + getProductLegacyPath(productSlug),
          permanent: false,
        },
      }
    }

    if (!parseResult.success || !enabledProducts[parseResult.data]) {
      const defaultProductSlug = enabledSlugs[0]

      return {
        redirect: {
          destination: `${basePath}/${defaultProductSlug}`,
          permanent: false,
        },
      }
    }

    const activeSlug = parseResult.data
    const previewParam = context.query.preview
    const isPreviewQueryEnabled =
      (Array.isArray(previewParam) ? previewParam[0] : previewParam) === 'true'
    const contentfulPreviewModeEnabled = Boolean(
      featureFlagVariableMap['contentful-preview-mode']?.value
    )
    const isPreviewEnabled = isPreviewQueryEnabled && contentfulPreviewModeEnabled

    const content = await fetchStaticPageContent<StaticPageEntry>({
      opCo,
      identifier: `rp-search-page-${activeSlug}`,
      locale: context.locale ?? 'en-GB',
      preview: isPreviewEnabled,
    })

    return {
      props: {
        activeSlug,
        enabledSlugs,
        content,
      },
    }
  } catch (error) {
    if (error instanceof GenericException) {
      logger.error(error.formatErrorForLogs('next_page_props'))
    }
    return {
      notFound: true,
    }
  }
}

function ProductPageLayout({ children }: React.PropsWithChildren) {
  const t = useTranslations('pages.search')
  return (
    <Layout title={t('page_title')} variant="no-section-wrapper">
      {children}
    </Layout>
  )
}

ProductPage.getLayout = function getLayout(page: React.ReactElement<React.PropsWithChildren>) {
  return <ProductPageLayout>{page}</ProductPageLayout>
}

export default withPageAuthRequired(ProductPage)
